// src/components/modules/ConversationModuleDetail.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  Clock,
  CheckCircle,
  ChevronRight,
  Target,
  Brain,
  BookOpen,
  Award,
  Lightbulb,
  Users,
  MessageSquare,
  ArrowRight,
  Play,
  Pause,
  RotateCcw,
  FileText,
  Beaker,
  TrendingUp,
  AlertCircle,
  Sparkles,
  Heart,
  Coffee,
  Eye,
  PartyPopper,
  Microscope,
  Zap,
  Gift,
  Shield,
  AlertTriangle,
  Compass,
  Scale,
  Wind,
  Wrench,
  Layers,
  Flame,
  MessageCircle
} from 'lucide-react';
import Card from '../shared/Card';
import Button from '../shared/Button';
import { useApp } from '../../context/AppContext';
import CompletionCertificate from './CompletionCertificate';
import CourseMasteryCertificate from './CourseMasteryCertificate';
import { useGamificationStore } from '../../stores/gamificationStore';
import { getAllModules } from '../../data/modules';
import { setReadingSection, saveModuleContext } from '../../services/coachModuleContext';

// Celebration Component
const CelebrationModal = ({ show, onClose, message = "Section Complete!" }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 1000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          exit={{ scale: 0, rotate: 180 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-gradient-to-br from-violet-500 to-fuchsia-600 p-8 rounded-2xl shadow-2xl"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex items-center justify-center mb-4"
          >
            <CheckCircle className="w-20 h-20 text-white" strokeWidth={2.5} />
          </motion.div>
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-2">{message}</h2>
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ repeat: Infinity, duration: 1 }}
            className="text-center"
          >
            <Sparkles className="w-8 h-8 text-yellow-300 mx-auto" />
          </motion.div>
        </motion.div>

        {/* Confetti particles */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            initial={{
              x: "50vw",
              y: "50vh",
              opacity: 1,
              scale: 1
            }}
            animate={{
              x: `${Math.random() * 100}vw`,
              y: `${Math.random() * 100}vh`,
              opacity: 0,
              scale: 0,
              rotate: Math.random() * 360
            }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="absolute w-3 h-3 rounded-full"
            style={{
              backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#95E1D3', '#F38181'][i % 5]
            }}
          />
        ))}
      </motion.div>
    </AnimatePresence>
  );
};

// Markdown parsing helper with special formatting blocks
const parseMarkdownText = (text) => {
  if (!text || typeof text !== 'string') return text;

  // Split text into lines first to handle headings and lists
  const lines = text.split('\n');
  const elements = [];
  let currentParagraph = [];

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      elements.push({ type: 'paragraph', content: currentParagraph.join('\n'), key: `p-${elements.length}` });
      currentParagraph = [];
    }
  };

  lines.forEach((line, idx) => {
    const trimmedLine = line.trim();

    // Handle headings
    if (trimmedLine.startsWith('###')) {
      flushParagraph();
      elements.push({ type: 'h3', content: trimmedLine.replace(/^###\s*/, ''), key: `h3-${idx}` });
    } else if (trimmedLine.startsWith('##')) {
      flushParagraph();
      elements.push({ type: 'h2', content: trimmedLine.replace(/^##\s*/, ''), key: `h2-${idx}` });
    } else if (trimmedLine.startsWith('**') && trimmedLine.includes('**')) {
      // Check if this is a standalone subheading or inline bold with content after
      const closingMarkerIndex = trimmedLine.indexOf('**', 2);
      if (closingMarkerIndex !== -1) {
        const afterBold = trimmedLine.slice(closingMarkerIndex + 2).trim();
        // Check if afterBold is just parenthetical subtitle text like "(Big vs. Small)" or just punctuation/colon
        const isJustSubtitle = afterBold.match(/^[\s:;\-—–]*(\([^)]+\))?[\s:;\-—–]*$/);

        // If there's substantial content after the bold part (not just subtitle or punctuation),
        // treat it as a regular paragraph with inline bold formatting
        if (afterBold.length > 2 && !isJustSubtitle && afterBold.replace(/^[:;\-—–\s]+/, '').length > 0) {
          // This is inline bold with content - treat as regular paragraph
          currentParagraph.push(line);
        } else {
          // This is a standalone subheading (possibly with subtitle in parentheses)
          flushParagraph();
          let content = trimmedLine.slice(2);
          const closingIndex = content.indexOf('**');
          if (closingIndex !== -1) {
            content = content.slice(0, closingIndex) + content.slice(closingIndex + 2);
          }
          elements.push({ type: 'subheading', content: content, key: `sub-${idx}` });
        }
      } else {
        // No closing **, treat as regular paragraph
        currentParagraph.push(line);
      }
    } else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      // List items
      flushParagraph();
      elements.push({ type: 'li', content: trimmedLine.slice(2), key: `li-${idx}` });
    } else if (trimmedLine === '') {
      // Empty line - flush current paragraph
      flushParagraph();
    } else {
      // Regular text - add to current paragraph
      currentParagraph.push(line);
    }
  });

  flushParagraph(); // Flush any remaining paragraph

  // Now process each element for special blocks
  const processedElements = [];

  elements.forEach((element) => {
    if (element.type === 'paragraph') {
      // Process paragraph for special blocks
      const specialBlockRegex = /\[([A-Z]+):([^\]]+)\]/g;
      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = specialBlockRegex.exec(element.content)) !== null) {
        // Add text before the special block
        if (match.index > lastIndex) {
          parts.push({ type: 'text', content: element.content.slice(lastIndex, match.index), key: `${element.key}-text-${lastIndex}` });
        }

        // Add the special block
        const [fullMatch, blockType, fullContent] = match;

        // Parse the content based on block type
        let parsedBlock = {
          type: blockType.toLowerCase(),
          key: `${element.key}-block-${match.index}`
        };

        if (blockType === 'COMPARE') {
          // COMPARE blocks have format: [COMPARE:weakTitle|weakContent|strongTitle|strongContent]
          const pipes = [];
          for (let i = 0; i < fullContent.length; i++) {
            if (fullContent[i] === '|') {
              pipes.push(i);
              if (pipes.length === 3) break;
            }
          }

          if (pipes.length === 3) {
            parsedBlock.weakTitle = fullContent.slice(0, pipes[0]).trim();
            parsedBlock.weakContent = fullContent.slice(pipes[0] + 1, pipes[1]).trim();
            parsedBlock.strongTitle = fullContent.slice(pipes[1] + 1, pipes[2]).trim();
            parsedBlock.strongContent = fullContent.slice(pipes[2] + 1).trim();
          } else {
            const pipeIndex = fullContent.indexOf('|');
            if (pipeIndex !== -1) {
              parsedBlock.title = fullContent.slice(0, pipeIndex).trim();
              parsedBlock.content = fullContent.slice(pipeIndex + 1).trim();
            } else {
              parsedBlock.content = fullContent.trim();
            }
          }
        } else {
          // STAT and QUOTE blocks have format: [TYPE:title|content]
          const pipeIndex = fullContent.indexOf('|');
          if (pipeIndex !== -1) {
            parsedBlock.title = fullContent.slice(0, pipeIndex).trim();
            parsedBlock.content = fullContent.slice(pipeIndex + 1).trim();
          } else {
            parsedBlock.content = fullContent.trim();
          }
        }

        parts.push(parsedBlock);
        lastIndex = match.index + fullMatch.length;
      }

      // Add remaining text
      if (lastIndex < element.content.length) {
        parts.push({ type: 'text', content: element.content.slice(lastIndex), key: `${element.key}-text-${lastIndex}` });
      }

      // If no special blocks found, treat as simple text
      if (parts.length === 0) {
        parts.push({ type: 'text', content: element.content, key: `${element.key}-text-all` });
      }

      // Add all paragraph parts to processed elements
      processedElements.push(...parts);
    } else {
      // Non-paragraph elements added directly
      processedElements.push(element);
    }
  });

  // Render all processed elements
  return processedElements.map((part) => {
    // Handle headings with colorful styling
    if (part.type === 'h2') {
      return (
        <h2 key={part.key} className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-pink-400 mt-8 mb-4">
          {parseSimpleMarkdown(part.content)}
        </h2>
      );
    }

    if (part.type === 'h3') {
      return (
        <h3 key={part.key} className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-400 mt-6 mb-3">
          {parseSimpleMarkdown(part.content)}
        </h3>
      );
    }

    if (part.type === 'subheading') {
      return (
        <div key={part.key} className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mt-5 mb-3">
          {parseSimpleMarkdown(part.content)}
        </div>
      );
    }

    if (part.type === 'li') {
      return (
        <div key={part.key} className="flex items-start gap-3 mb-2 ml-4">
          <div className="w-2 h-2 rounded-full bg-gradient-to-r from-rose-500 to-pink-500 mt-2 flex-shrink-0"></div>
          <div className="text-slate-300">{parseSimpleMarkdown(part.content)}</div>
        </div>
      );
    }

    // Special blocks below
    if (part.type === 'stat') {
      return (
        <div key={part.key} className="my-6 p-4 bg-gradient-to-r from-rose-500/10 to-pink-500/10 border-l-4 border-rose-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <TrendingUp className="w-6 h-6 text-rose-400 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold text-rose-300 text-sm uppercase tracking-wider mb-1">{part.title}</div>
              <div className="text-slate-200 leading-relaxed">{parseSimpleMarkdown(part.content)}</div>
            </div>
          </div>
        </div>
      );
    }

    if (part.type === 'quote') {
      return (
        <div key={part.key} className="my-6 p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-l-4 border-purple-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <MessageSquare className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold text-purple-300 text-sm mb-1">{part.title}</div>
              <div className="text-slate-200 italic leading-relaxed">"{parseSimpleMarkdown(part.content)}"</div>
            </div>
          </div>
        </div>
      );
    }

    if (part.type === 'research') {
      return (
        <div key={part.key} className="my-6 p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-l-4 border-purple-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <Microscope className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold text-indigo-300 text-sm uppercase tracking-wider mb-1">{part.title}</div>
              <div className="text-slate-200 leading-relaxed">{parseSimpleMarkdown(part.content)}</div>
            </div>
          </div>
        </div>
      );
    }

    if (part.type === 'observation') {
      return (
        <div key={part.key} className="my-6 p-4 bg-gradient-to-r from-pink-500/10 to-purple-500/10 border-l-4 border-pink-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <Eye className="w-6 h-6 text-pink-400 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold text-pink-300 text-sm uppercase tracking-wider mb-1">{part.title}</div>
              <div className="text-slate-200 leading-relaxed">{parseSimpleMarkdown(part.content)}</div>
            </div>
          </div>
        </div>
      );
    }

    if (part.type === 'insight') {
      return (
        <div key={part.key} className="my-6 p-4 bg-gradient-to-r from-violet-500/10 to-purple-500/10 border-l-4 border-violet-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <Zap className="w-6 h-6 text-violet-400 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold text-violet-300 text-sm uppercase tracking-wider mb-1">{part.title}</div>
              <div className="text-slate-200 leading-relaxed">{parseSimpleMarkdown(part.content)}</div>
            </div>
          </div>
        </div>
      );
    }

    if (part.type === 'compare') {
      // New format with explicit fields
      if (part.weakTitle && part.strongTitle) {
        return (
          <div key={part.key} className="my-6 grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="font-bold text-red-400 text-sm mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {part.weakTitle}
              </div>
              <div className="text-slate-300 text-sm">{parseSimpleMarkdown(part.weakContent)}</div>
            </div>
            <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-lg">
              <div className="font-bold text-violet-400 text-sm mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {part.strongTitle}
              </div>
              <div className="text-slate-300 text-sm">{parseSimpleMarkdown(part.strongContent)}</div>
            </div>
          </div>
        );
      }

      // Fallback for old format
      const [weak, strong] = (part.content || '').split('|').map(s => s.trim());
      return (
        <div key={part.key} className="my-6 grid md:grid-cols-2 gap-4">
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="font-bold text-red-400 text-sm mb-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {part.title?.split('|')[0] || 'Weak'}
            </div>
            <div className="text-slate-300 text-sm">{parseSimpleMarkdown(weak)}</div>
          </div>
          <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-lg">
            <div className="font-bold text-violet-400 text-sm mb-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              {part.title?.split('|')[1] || 'Strong'}
            </div>
            <div className="text-slate-300 text-sm">{parseSimpleMarkdown(strong)}</div>
          </div>
        </div>
      );
    }

    // Regular text with bold/italic
    return <span key={part.key}>{parseSimpleMarkdown(part.content)}</span>;
  });
};

// Helper to parse simple markdown (bold/italic) within text
const parseSimpleMarkdown = (text) => {
  if (!text || typeof text !== 'string') return text;

  // Handle bold (**text**)
  const parts = text.split(/(\*\*.*?\*\*)/);

  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      const boldText = part.slice(2, -2);
      return <strong key={`bold-${index}`} className="font-bold text-white">{boldText}</strong>;
    }

    // Handle italics (*text*)
    const italicParts = part.split(/(\*[^*]+\*)/);

    if (italicParts.length > 1) {
      return italicParts.map((subPart, subIndex) => {
        if (subPart.startsWith('*') && subPart.endsWith('*') && subPart.length > 2) {
          const italicText = subPart.slice(1, -1);
          return <em key={`${index}-italic-${subIndex}`} className="italic text-purple-300">{italicText}</em>;
        }
        return <span key={`${index}-text-${subIndex}`}>{subPart}</span>;
      });
    }

    return <span key={`text-${index}`}>{part}</span>;
  });
};

const MarkdownText = ({ children, className = "" }) => {
  if (!children) return null;

  // Split by double line breaks to create paragraphs
  const paragraphs = children.split('\n\n').filter(p => p.trim());

  return (
    <div className={className}>
      {paragraphs.map((paragraph, idx) => (
        <div key={idx} className={idx > 0 ? 'mt-4' : ''}>
          {parseMarkdownText(paragraph)}
        </div>
      ))}
    </div>
  );
};

// Component for rendering sections with expandable content
const SectionContent = ({ section }) => {
  const [expandedParts, setExpandedParts] = useState({
    scientificFoundation: false,
    deepDive: false,
    practicalApplication: false,
    commonMistakes: false
  });

  const toggleExpanded = (part) => {
    setExpandedParts(prev => ({
      ...prev,
      [part]: !prev[part]
    }));
  };

  return (
    <Card className="p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <h3 className="text-xl sm:text-2xl font-bold text-white">{section.title}</h3>
          <span className="text-xs sm:text-sm text-slate-400">({section.duration})</span>
        </div>
      </div>

      <div className="space-y-6">
        {/* Introduction */}
        <div className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 border-l-4 border-indigo-500 rounded-r-xl p-6">
          <MarkdownText className="text-slate-200 text-base sm:text-lg leading-relaxed first-letter:text-3xl sm:first-letter:text-5xl first-letter:font-bold first-letter:text-indigo-400 first-letter:mr-2 sm:first-letter:mr-3 first-letter:float-left">
            {section.content.intro}
          </MarkdownText>
        </div>

        {/* Main Content */}
        {section.content.mainContent && (
          <div>
            <MarkdownText className="text-slate-300 text-sm sm:text-base leading-relaxed whitespace-pre-line">
              {section.content.mainContent}
            </MarkdownText>
          </div>
        )}

        {/* Scientific Foundation */}
        {section.content.scientificFoundation && (
          <div className="bg-gradient-to-r from-rose-500/20 to-pink-500/20 border-2 border-rose-400/40 rounded-xl p-6 hover:border-rose-400/60 transition-all shadow-lg shadow-rose-500/10">
            <button
              onClick={() => toggleExpanded('scientificFoundation')}
              className="w-full text-left group"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-base sm:text-lg font-bold text-rose-200 flex items-center gap-2 sm:gap-3 group-hover:text-rose-100 transition-colors">
                  <Beaker className="w-5 h-5 sm:w-6 sm:h-6 text-rose-400" />
                  {section.content.scientificFoundation.title}
                </h4>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-rose-300 opacity-0 group-hover:opacity-100 transition-opacity">
                    {expandedParts.scientificFoundation ? 'Click to collapse' : 'Click to expand'}
                  </span>
                  <ChevronRight
                    className={`w-6 h-6 text-rose-400 transition-transform group-hover:text-rose-300 ${
                      expandedParts.scientificFoundation ? 'rotate-90' : ''
                    }`}
                  />
                </div>
              </div>
            </button>
            
            <AnimatePresence>
              {expandedParts.scientificFoundation && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4 space-y-4">
                    <MarkdownText className="text-slate-300 text-sm sm:text-base leading-relaxed whitespace-pre-line">
                      {section.content.scientificFoundation.content}
                    </MarkdownText>
                    
                    {/* Key Studies if present */}
                    {section.content.scientificFoundation.keyStudies && (
                      <div className="mt-6 space-y-3">
                        <h5 className="text-sm font-medium text-rose-300 uppercase tracking-wider">Key Research</h5>
                        {section.content.scientificFoundation.keyStudies.map((study, idx) => (
                          <div key={idx} className="p-3 bg-rose-900/20 rounded-lg border border-rose-500/20">
                            <div className="text-white font-medium">{study.researcher}</div>
                            <div className="text-rose-200 text-sm">{study.institution}, {study.year}</div>
                            <div className="text-slate-300 text-sm mt-1">{study.finding}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Key Points */}
        {section.content.keyPoints && section.content.keyPoints.length > 0 && (
          <div>
            <h4 className="text-base sm:text-lg font-medium text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
              Key Insights
            </h4>
            <div className="space-y-3">
              {section.content.keyPoints.map((point, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-purple-500/10 rounded-lg">
                  <div className="w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-purple-300 text-sm font-bold">{index + 1}</span>
                  </div>
                  <MarkdownText className="text-slate-300 text-sm sm:text-base">
                    {point}
                  </MarkdownText>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Deep Dive */}
        {section.content.deepDive && (
          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-2 border-purple-400/40 rounded-xl p-6 hover:border-purple-400/60 transition-all shadow-lg shadow-purple-500/10">
            <button
              onClick={() => toggleExpanded('deepDive')}
              className="w-full text-left group"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-base sm:text-lg font-bold text-purple-200 flex items-center gap-2 sm:gap-3 group-hover:text-purple-100 transition-colors">
                  <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400" />
                  {section.content.deepDive.title}
                </h4>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-purple-300 opacity-0 group-hover:opacity-100 transition-opacity">
                    {expandedParts.deepDive ? 'Click to collapse' : 'Click to expand'}
                  </span>
                  <ChevronRight
                    className={`w-6 h-6 text-purple-400 transition-transform group-hover:text-purple-300 ${
                      expandedParts.deepDive ? 'rotate-90' : ''
                    }`}
                  />
                </div>
              </div>
            </button>
            
            <AnimatePresence>
              {expandedParts.deepDive && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4">
                    <MarkdownText className="text-slate-300 leading-relaxed whitespace-pre-line">
                      {section.content.deepDive.content}
                    </MarkdownText>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Practical Application */}
        {section.content.practicalApplication && (
          <div className="bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 border-2 border-violet-400/40 rounded-xl p-6 hover:border-violet-400/60 transition-all shadow-lg shadow-violet-500/10">
            <button
              onClick={() => toggleExpanded('practicalApplication')}
              className="w-full text-left group"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-base sm:text-lg font-bold text-violet-200 flex items-center gap-2 sm:gap-3 group-hover:text-violet-100 transition-colors">
                  <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6 text-violet-400" />
                  {section.content.practicalApplication.title}
                </h4>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-violet-300 opacity-0 group-hover:opacity-100 transition-opacity">
                    {expandedParts.practicalApplication ? 'Click to collapse' : 'Click to expand'}
                  </span>
                  <ChevronRight
                    className={`w-6 h-6 text-violet-400 transition-transform group-hover:text-violet-300 ${
                      expandedParts.practicalApplication ? 'rotate-90' : ''
                    }`}
                  />
                </div>
              </div>
            </button>
            
            <AnimatePresence>
              {expandedParts.practicalApplication && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4 space-y-4">
                    {/* Handle simple content format */}
                    {section.content.practicalApplication.content && (
                      <MarkdownText className="text-slate-300 leading-relaxed whitespace-pre-line">
                        {section.content.practicalApplication.content}
                      </MarkdownText>
                    )}

                    {/* Handle structured format with description/steps/tips */}
                    {section.content.practicalApplication.description && (
                      <p className="text-slate-300 leading-relaxed">
                        {section.content.practicalApplication.description}
                      </p>
                    )}

                    {section.content.practicalApplication.steps && (
                      <div>
                        <h5 className="text-sm font-medium text-violet-300 uppercase tracking-wider mb-3">Steps</h5>
                        <ul className="space-y-2">
                          {section.content.practicalApplication.steps.map((step, idx) => (
                            <li key={idx} className="flex gap-3 items-start">
                              <div className="w-6 h-6 bg-violet-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="text-violet-300 text-xs font-bold">{idx + 1}</span>
                              </div>
                              <span className="text-slate-300">{step}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {section.content.practicalApplication.tips && (
                      <div className="mt-4 p-4 bg-violet-900/20 rounded-lg border border-violet-500/20">
                        <h5 className="text-sm font-medium text-violet-300 uppercase tracking-wider mb-2 flex items-center gap-2">
                          <Lightbulb className="w-4 h-4" />
                          Tips
                        </h5>
                        <ul className="space-y-2">
                          {section.content.practicalApplication.tips.map((tip, idx) => (
                            <li key={idx} className="flex gap-2 items-start text-slate-300 text-sm">
                              <span className="text-violet-400">•</span>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Common Mistakes */}
        {section.content.commonMistakes && (
          <div className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 border-2 border-pink-400/40 rounded-xl p-6 hover:border-pink-400/60 transition-all shadow-lg shadow-pink-500/10">
            <button
              onClick={() => toggleExpanded('commonMistakes')}
              className="w-full text-left group"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-base sm:text-lg font-bold text-pink-200 flex items-center gap-2 sm:gap-3 group-hover:text-pink-100 transition-colors">
                  <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-pink-400" />
                  {section.content.commonMistakes.title}
                </h4>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-pink-300 opacity-0 group-hover:opacity-100 transition-opacity">
                    {expandedParts.commonMistakes ? 'Click to collapse' : 'Click to expand'}
                  </span>
                  <ChevronRight
                    className={`w-6 h-6 text-pink-400 transition-transform group-hover:text-pink-300 ${
                      expandedParts.commonMistakes ? 'rotate-90' : ''
                    }`}
                  />
                </div>
              </div>
            </button>
            
            <AnimatePresence>
              {expandedParts.commonMistakes && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4">
                    <MarkdownText className="text-slate-300 leading-relaxed whitespace-pre-line">
                      {section.content.commonMistakes.content}
                    </MarkdownText>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </Card>
  );
};

// Exercise Card Component
const ExerciseCard = ({ exercise, index, isCompleted, onComplete, isActive, onClick }) => {
  const typeConfig = {
    observation: {
      color: 'rose',
      icon: Eye,
      gradient: 'from-rose-500/20 via-pink-500/20 to-purple-500/20',
      border: 'border-rose-500/40',
      iconBg: 'bg-gradient-to-br from-rose-500/30 to-pink-500/30'
    },
    practice: {
      color: 'green',
      icon: Target,
      gradient: 'from-violet-500/20 via-fuchsia-500/20 to-purple-500/20',
      border: 'border-violet-500/40',
      iconBg: 'bg-gradient-to-br from-violet-500/30 to-fuchsia-500/30'
    },
    reflection: {
      color: 'purple',
      icon: Brain,
      gradient: 'from-purple-500/20 via-violet-500/20 to-pink-500/20',
      border: 'border-purple-500/40',
      iconBg: 'bg-gradient-to-br from-purple-500/30 to-violet-500/30'
    },
    'self-assessment': {
      color: 'pink',
      icon: Lightbulb,
      gradient: 'from-pink-500/20 via-purple-500/20 to-rose-500/20',
      border: 'border-pink-500/40',
      iconBg: 'bg-gradient-to-br from-pink-500/30 to-purple-500/30'
    }
  };

  const config = typeConfig[exercise.type] || typeConfig.practice;
  const ExerciseIcon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Card
        hover={true}
        className={`p-0 cursor-pointer transition-all overflow-hidden ${
          isActive ? 'ring-2 ring-conversation-teal shadow-xl shadow-rose-500/20' : ''
        } ${isCompleted ? 'border-violet-500/50' : ''}`}
        onClick={onClick}
      >
        {/* Header with gradient background */}
        <div className={`bg-gradient-to-r ${config.gradient} p-6 relative`}>
          <div className="flex items-start gap-4">
            <motion.div
              className={`w-16 h-16 ${config.iconBg} rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg`}
              whileHover={{ scale: 1.05, rotate: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <ExerciseIcon className={`w-8 h-8 text-${config.color}-300`} />
            </motion.div>

            <div className="flex-1">
              <div className="flex items-start justify-between gap-3 mb-3">
                <h4 className="text-lg sm:text-xl font-bold text-white leading-tight">{exercise.title}</h4>
                {isCompleted && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring" }}
                  >
                    <CheckCircle className="w-6 h-6 text-violet-400 flex-shrink-0" />
                  </motion.div>
                )}
              </div>

              <div className="flex items-center flex-wrap gap-2 mb-3">
                {exercise.type && (
                  <span className={`px-3 py-1 bg-${config.color}-500/30 text-${config.color}-200 rounded-full text-xs font-medium border border-${config.color}-400/30`}>
                    {exercise.type.replace('_', ' ').replace('-', ' ')}
                  </span>
                )}
                {exercise.duration && (
                  <span className="flex items-center gap-1 px-3 py-1 bg-white/10 rounded-full text-xs text-slate-300">
                    <Clock className="w-3 h-3" />
                    {exercise.duration}
                  </span>
                )}
                {!isActive && (
                  <span className="flex items-center gap-1 px-3 py-1 bg-white/10 rounded-full text-xs text-slate-300">
                    <ChevronRight className="w-3 h-3" />
                    Click to expand
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="p-6 pb-4">
          <MarkdownText className="text-slate-300 leading-relaxed">
            {exercise.description}
          </MarkdownText>
        </div>

          {isActive && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="px-6 pb-6"
            >
              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent mb-6" />

              {/* Instructions with visual steps */}
              <div className="mb-6">
                <h5 className="text-white font-bold text-base sm:text-lg mb-4 flex items-center gap-2">
                  <Target className="w-4 h-4 sm:w-5 sm:h-5 text-rose-400" />
                  Step-by-Step Instructions
                </h5>
                <div className="space-y-3">
                  {(exercise.steps || exercise.instructions || []).map((instruction, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-slate-800/50 rounded-lg border border-slate-700/50 hover:border-rose-500/50 transition-all group"
                    >
                      <div className="flex-shrink-0">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center text-white font-bold text-xs sm:text-sm shadow-lg">
                          {idx + 1}
                        </div>
                      </div>
                      <div className="flex-1 pt-1">
                        <MarkdownText className="text-slate-200 text-sm sm:text-base group-hover:text-white transition-colors">
                          {instruction}
                        </MarkdownText>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Science Connection */}
              {exercise.scienceConnection && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 sm:p-5 bg-gradient-to-br from-rose-900/30 to-pink-900/30 rounded-xl border-2 border-rose-500/30 hover:border-rose-400/50 transition-all"
                >
                  <h6 className="text-rose-300 font-bold text-sm sm:text-base mb-3 flex items-center gap-2">
                    <Beaker className="w-4 h-4 sm:w-5 sm:h-5" />
                    Science Connection
                  </h6>
                  <MarkdownText className="text-slate-200 text-sm sm:text-base leading-relaxed">
                    {exercise.scienceConnection}
                  </MarkdownText>
                </motion.div>
              )}

              {/* Real World Variation */}
              {exercise.realWorldVariation && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="mb-6 p-4 sm:p-5 bg-gradient-to-br from-violet-900/30 to-fuchsia-900/30 rounded-xl border-2 border-violet-500/30 hover:border-violet-400/50 transition-all"
                >
                  <h6 className="text-violet-300 font-bold text-sm sm:text-base mb-3 flex items-center gap-2">
                    <Coffee className="w-4 h-4 sm:w-5 sm:h-5" />
                    Real World Variation
                  </h6>
                  <MarkdownText className="text-slate-200 text-sm sm:text-base leading-relaxed">
                    {exercise.realWorldVariation}
                  </MarkdownText>
                </motion.div>
              )}

              {/* Reflection Questions */}
              {exercise.reflection && exercise.reflection.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mb-6 p-4 sm:p-5 bg-gradient-to-br from-purple-900/30 to-violet-900/30 rounded-xl border-2 border-purple-500/30 hover:border-purple-400/50 transition-all"
                >
                  <h6 className="text-purple-300 font-bold text-sm sm:text-base mb-3 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5" />
                    Reflection Questions
                  </h6>
                  <ul className="space-y-3">
                    {exercise.reflection.map((question, idx) => (
                      <li key={idx} className="flex gap-3 items-start">
                        <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-purple-400 text-xs font-bold">{idx + 1}</span>
                        </div>
                        <MarkdownText className="text-slate-200 text-sm sm:text-base flex-1">
                          {question}
                        </MarkdownText>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}

              {/* Mark Complete Button */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                {!isCompleted ? (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      onComplete();
                    }}
                    className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white font-semibold px-6 py-2 shadow-lg hover:shadow-violet-500/50 transition-all"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Complete
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 text-violet-400">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Completed!</span>
                  </div>
                )}
              </div>
            </motion.div>
          )}
      </Card>
    </motion.div>
  );
};

// Main Module Detail Component
const ConversationModuleDetail = ({ module, onBack, onComplete }) => {
  const { 
    getModuleProgress, 
    updateModuleProgress,
    completeModule,
    isModuleCompleted
  } = useApp();
  
  const savedProgress = getModuleProgress(module.id) || {};
  const moduleIsCompleted = isModuleCompleted(module.id);
  
  const [currentPage, setCurrentPage] = useState(savedProgress.currentPage || 0);
  const [completedSections, setCompletedSections] = useState(savedProgress.completedSections || []);
  const [completedExercises, setCompletedExercises] = useState(savedProgress.completedExercises || []);
  const [activeExercise, setActiveExercise] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationMessage, setCelebrationMessage] = useState("Section Complete!");
  const [showCertificate, setShowCertificate] = useState(false);
  const [showCourseMastery, setShowCourseMastery] = useState(false);
  
  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);
  
  // Create pages from module content
  const pages = [
    { type: 'overview', title: 'Overview', icon: BookOpen },
    ...module.content.sections.map((section, idx) => ({
      type: 'section',
      index: idx,
      title: section.title,
      icon: Brain
    })),
    { type: 'exercises', title: 'Practice Exercises', icon: Target },
    // Only include Tools page if module has tools
    ...(module.tools && module.tools.length > 0 ? [{ type: 'tools', title: 'Interactive Tools', icon: Wrench }] : []),
    // Only include FAQ page if module has FAQs
    ...(module.faqs && module.faqs.length > 0 ? [{ type: 'faqs', title: 'FAQs', icon: MessageSquare }] : []),
    // Only include Resources page if module has resources
    ...(module.resources && module.resources.length > 0 ? [{ type: 'resources', title: 'Resources', icon: BookOpen }] : []),
    { type: 'takeaways', title: 'Key Takeaways', icon: Award },
    // Add Next Steps page before certificate
    { type: 'nextsteps', title: 'Next Steps', icon: ArrowRight }
  ];

  const currentPageData = pages[currentPage];

  // The page being read is the section the coach hears about if the person
  // opens it from here (the overview names no section).
  const readingSection = currentPageData && currentPageData.type !== 'overview' ? currentPageData.title : null;
  useEffect(() => {
    setReadingSection(module.id, readingSection);
    return () => setReadingSection(module.id, null);
  }, [module.id, readingSection]);
  
  // Calculate progress
  const totalItems =
    module.content.sections.length +
    (module.exercises?.length || 0);

  const completedItems =
    completedSections.length +
    completedExercises.length;
  
  const progressPercentage = Math.round((completedItems / totalItems) * 100);

  // Save progress
  useEffect(() => {
    const saveProgress = async () => {
      await updateModuleProgress(module.id, {
        currentPage,
        completedSections,
        completedExercises,
        progressPercentage,
        lastUpdated: new Date().toISOString()
      });
    };

    const timeoutId = setTimeout(saveProgress, 500);
    return () => clearTimeout(timeoutId);
  }, [currentPage, completedSections, completedExercises, module.id]);

  const handleSectionComplete = (sectionIndex) => {
    if (!completedSections.includes(sectionIndex)) {
      setCompletedSections([...completedSections, sectionIndex]);
    }
  };

  const handleExerciseComplete = (exerciseIndex) => {
    if (!completedExercises.includes(exerciseIndex)) {
      setCompletedExercises([...completedExercises, exerciseIndex]);
    }
  };

  const handleModuleComplete = async () => {
    await completeModule(module.id, {
      completedSections,
      completedExercises,
      progressPercentage: 100,
      completedAt: new Date().toISOString()
    });

    // Award gamification points for module completion
    const gamification = useGamificationStore.getState();
    gamification.completeModule(module.id);

    // Check if all modules are now complete (get fresh state after completeModule)
    const updatedGamification = useGamificationStore.getState();
    const allModules = getAllModules();
    const completedModulesList = updatedGamification.completedModules;

    console.log('Completed modules:', completedModulesList);
    console.log('All module IDs:', allModules.map(m => m.id));

    const allComplete = allModules.every(m => completedModulesList.includes(m.id));

    console.log('All complete?', allComplete, 'Module count:', allModules.length);

    // Show special course mastery certificate if all 8 modules complete
    if (allComplete && allModules.length === 8) {
      console.log('🏆 SHOWING COURSE MASTERY CERTIFICATE!');
      // Award bonus points for course completion
      updatedGamification.addPoints(500, 'Course Mastery Achieved! 🏆');
      setShowCourseMastery(true);
    } else {
      console.log('Showing regular module certificate');
      // Show regular module certificate
      setShowCertificate(true);
    }

    // Call onComplete callback after a delay (after user closes certificate)
    // onComplete?.();
  };

  const handleContinue = () => {
    const pageType = currentPageData.type;

    // Auto-complete current page
    if (pageType === 'section' && !completedSections.includes(currentPageData.index)) {
      handleSectionComplete(currentPageData.index);
    } else if (pageType === 'exercises' && module.exercises && module.exercises.length > 0) {
      // Mark all exercises as complete
      const allExerciseIndices = module.exercises.map((_, idx) => idx);
      setCompletedExercises(allExerciseIndices);
    }

    // If on last page, complete the module
    if (currentPage === pages.length - 1) {
      // Show celebration first
      setCelebrationMessage("Module Complete! 🎉");
      setShowCelebration(true);

      // Complete module after celebration
      setTimeout(() => {
        handleModuleComplete();
      }, 2000);
    } else {
      // Show celebration for regular page completion
      setCelebrationMessage("Well Done! 🌟");
      setShowCelebration(true);

      // Move to next page after celebration
      setTimeout(() => {
        setCurrentPage(currentPage + 1);
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 pt-24 md:pt-32">
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-20 sm:pb-12 max-w-6xl">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-pearl/60 hover:text-pearl mb-8 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Modules
        </button>

        {/* Module Header with Full-Width Banner Image */}
        <div className="relative mb-8 rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-slate-800 to-slate-900">
          {/* Full-Width Banner Image */}
          {module.heroImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="relative h-48 md:h-64"
            >
              <img
                src={module.heroImage}
                alt={module.title}
                className="w-full h-full object-cover"
              />
              {/* Gradient overlay for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />
            </motion.div>
          )}

          {/* Content */}
          <div className={`relative p-8 ${module.heroImage ? '-mt-16 md:-mt-20' : ''}`}>
            <div className="flex flex-col items-center text-center mb-6">
              {/* Fallback icon if no hero image */}
              {!module.heroImage && (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  whileHover={{ scale: 1.05, rotate: 5 }}
                  className="w-32 h-32 bg-gradient-to-br from-rose-500 via-pink-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg mb-6"
                >
                  <BookOpen className="w-16 h-16 text-white" />
                </motion.div>
              )}

              <div className="mb-6">
                <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">
                  {module.title}
                </h1>
                <p className="text-xl text-pink-300">{module.subtitle}</p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-6">
              <div className="flex-1 w-full">

                <MarkdownText className="text-pearl/90 mb-6 text-base md:text-lg leading-relaxed text-center">
                  {module.description}
                </MarkdownText>

                <div className="flex items-center justify-center flex-wrap gap-2 md:gap-4 text-xs md:text-sm">
                  <span className="flex items-center gap-1 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                    <Clock className="w-3 h-3 md:w-4 md:h-4 text-rose-400" />
                    <span className="text-pearl font-medium">{module.duration}</span>
                  </span>
                  {module.difficulty && (
                    <span className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full font-medium border backdrop-blur-sm ${
                      module.difficulty === 'Foundation'
                        ? 'bg-violet-500/20 text-violet-300 border-violet-500/30'
                        : module.difficulty === 'Essential'
                        ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                        : 'bg-red-500/20 text-red-300 border-red-500/30'
                    }`}>
                      {module.difficulty}
                    </span>
                  )}
                  {module.prerequisites && module.prerequisites.length > 0 && (
                    <span className="px-3 md:px-4 py-1.5 md:py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 text-pearl">
                      Prerequisites: {module.prerequisites.length}
                    </span>
                  )}
                </div>
              </div>

              {/* Progress Circle */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex-shrink-0"
              >
                <div className="relative w-24 h-24 md:w-32 md:h-32 bg-white/10 backdrop-blur-sm rounded-full p-2 border-2 border-white/20">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 112 112">
                    <circle
                      cx="56" cy="56" r="50"
                      fill="none"
                      stroke="rgba(255,255,255,0.1)"
                      strokeWidth="8"
                    />
                    <motion.circle
                      cx="56" cy="56" r="50"
                      fill="none"
                      stroke="url(#progressGradient)"
                      strokeWidth="8"
                      strokeDasharray={`${2 * Math.PI * 50}`}
                      strokeDashoffset={`${2 * Math.PI * 50 * (1 - progressPercentage/100)}`}
                      strokeLinecap="round"
                      initial={{ strokeDashoffset: `${2 * Math.PI * 50}` }}
                      animate={{ strokeDashoffset: `${2 * Math.PI * 50 * (1 - progressPercentage/100)}` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                    />
                    <defs>
                      <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f43f5e" />
                        <stop offset="100%" stopColor="#ec4899" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl md:text-3xl font-bold text-white">{progressPercentage}%</span>
                    {moduleIsCompleted && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring" }}
                      >
                        <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-violet-400 mt-1" />
                      </motion.div>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Progress Dots Only */}
        <div className="flex flex-wrap items-center justify-center mb-8">
          {pages.map((page, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentPage(idx)}
              className="group h-7 min-w-[28px] px-2 flex items-center justify-center"
              title={page.title}
              aria-label={`Page ${idx + 1}: ${page.title}`}
              aria-current={idx === currentPage ? 'step' : undefined}
            >
              <span
                className={`block rounded-full transition-all group-hover:scale-125 ${
                  idx === currentPage
                    ? 'w-10 h-3 bg-gradient-to-r from-rose-500 to-pink-500'
                    : idx < currentPage
                    ? 'w-3 h-3 bg-violet-500'
                    : 'w-3 h-3 bg-slate-600 group-hover:bg-slate-500'
                }`}
              />
            </button>
          ))}
        </div>

        {/* Page Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="mb-6"
          >
            {/* Overview Page */}
            {currentPageData.type === 'overview' && (
              <Card className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Module Overview</h2>

                <div className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 border-l-4 border-indigo-500 rounded-r-xl p-6 mb-8">
                  <MarkdownText className="text-slate-200 text-lg leading-relaxed first-letter:text-5xl first-letter:font-bold first-letter:text-indigo-400 first-letter:mr-3 first-letter:float-left">
                    {module.content.overview}
                  </MarkdownText>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-violet-400" />
                    Learning Objectives
                  </h3>
                  <ul className="space-y-3">
                    {module.content.learningObjectives.map((objective, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-violet-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-rose-400 text-sm">{idx + 1}</span>
                        </div>
                        <MarkdownText className="text-pearl/80">
                          {objective}
                        </MarkdownText>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            )}

            {/* Section Pages */}
            {currentPageData.type === 'section' && (
              <SectionContent
                section={module.content.sections[currentPageData.index]}
                sectionIndex={currentPageData.index}
                isCompleted={completedSections.includes(currentPageData.index)}
                onComplete={() => handleSectionComplete(currentPageData.index)}
              />
            )}

            {/* Exercises Page */}
            {currentPageData.type === 'exercises' && (
              <div className="space-y-6">
                {/* Enhanced header */}
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="p-8 bg-gradient-to-br from-slate-800/50 to-slate-900/50 border-2 border-rose-500/30">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-xl">
                        <Target className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-3xl font-bold text-white mb-2">Practice Exercises</h2>
                        <p className="text-slate-300 text-lg">
                          Apply what you've learned with hands-on practice
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-rose-500/10 to-pink-500/10 rounded-lg border border-rose-500/20">
                      <Lightbulb className="w-6 h-6 text-pink-400 flex-shrink-0" />
                      <p className="text-slate-200 text-sm">
                        <strong className="text-white">Tip:</strong> Click any exercise card to expand full instructions, science connections, and reflection questions.
                        Each exercise type has its own colour coding to help you navigate.
                      </p>
                    </div>

                    {/* Enhanced Progress indicator */}
                    <div className="mt-6 pt-6 border-t border-slate-700">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-slate-300">Exercise Progress</span>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-white">
                            {completedExercises.length}
                          </span>
                          <span className="text-sm text-slate-400">of</span>
                          <span className="text-lg font-bold text-slate-300">
                            {(module.exercises || []).length}
                          </span>
                          <span className="text-sm text-slate-400">completed</span>
                        </div>
                      </div>

                      {/* Progress bar with glow */}
                      <div className="relative">
                        <div className="w-full h-4 bg-slate-800 rounded-full overflow-hidden border border-slate-700 shadow-inner">
                          <motion.div
                            className="h-full bg-gradient-to-r from-rose-500 via-pink-400 to-pink-500 relative shadow-lg"
                            initial={{ width: 0 }}
                            animate={{
                              width: `${((completedExercises.length / (module.exercises || []).length) * 100)}%`
                            }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            style={{
                              boxShadow: '0 0 20px rgba(20, 184, 166, 0.6), 0 0 40px rgba(212, 175, 55, 0.4)'
                            }}
                          >
                            {/* Animated shine effect */}
                            <motion.div
                              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                              animate={{
                                x: ['-100%', '200%']
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                repeatDelay: 1,
                                ease: "easeInOut"
                              }}
                            />
                          </motion.div>
                        </div>

                        {/* Percentage badge */}
                        <motion.div
                          className="absolute -top-1 bg-gradient-to-r from-rose-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg border-2 border-slate-900"
                          initial={{ left: 0 }}
                          animate={{
                            left: `calc(${((completedExercises.length / (module.exercises || []).length) * 100)}% - 28px)`
                          }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                          {Math.round((completedExercises.length / (module.exercises || []).length) * 100)}%
                        </motion.div>
                      </div>

                      {/* Completion message */}
                      {completedExercises.length === (module.exercises || []).length && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-3 flex items-center justify-center gap-2 text-violet-400"
                        >
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">All exercises completed! Great work! 🎉</span>
                        </motion.div>
                      )}
                    </div>
                  </Card>
                </motion.div>

                {/* Exercise cards */}
                {(module.exercises || []).map((exercise, idx) => (
                  <ExerciseCard
                    key={idx}
                    exercise={exercise}
                    index={idx}
                    isCompleted={completedExercises.includes(idx)}
                    onComplete={() => handleExerciseComplete(idx)}
                    isActive={activeExercise === idx}
                    onClick={() => setActiveExercise(activeExercise === idx ? null : idx)}
                  />
                ))}
              </div>
            )}

            {/* Tools Page */}
            {currentPageData.type === 'tools' && (
              <div className="space-y-6">
                <Card className="p-6">
                  <h2 className="text-2xl font-bold text-white mb-4">Interactive Practice Tools</h2>
                  <p className="text-slate-300 mb-6">
                    These interactive tools help you practise the skills you've learned in this module. Click any tool to open it in the full Toolkit.
                  </p>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {(module.tools || []).map((tool, idx) => {
                    const IconComponent = {
                      Heart, TrendingUp, Gift, Target, Eye, Shield, AlertTriangle,
                      Compass, MessageCircle, Scale, Wind, Wrench, Layers, Users, Flame
                    }[tool.icon] || Target;

                    return (
                      <Card key={idx} className="p-6 hover:border-rose-500/50 transition-all cursor-pointer" hover={true}
                        onClick={() => {
                          // Navigate to toolkit page and scroll to tool
                          window.location.href = `/toolkit#${tool.id}`;
                        }}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 bg-gradient-to-br from-rose-500/20 to-pink-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-7 h-7 text-rose-400" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-white mb-2">
                              {tool.title}
                            </h3>
                            <p className="text-slate-300 text-sm mb-3">
                              {tool.description}
                            </p>
                            <div className="flex items-center gap-3">
                              <span className="flex items-center gap-1 text-xs text-slate-400">
                                <Clock className="w-3 h-3" />
                                {tool.estimatedTime}
                              </span>
                              <span className="text-rose-400 hover:text-pink-400 text-sm flex items-center gap-1 transition-colors">
                                Open Tool
                                <ArrowRight className="w-4 h-4" />
                              </span>
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* FAQs Page */}
            {currentPageData.type === 'faqs' && (
              <div className="space-y-6">
                <Card className="p-6">
                  <h2 className="text-2xl font-bold text-white mb-4">Frequently Asked Questions</h2>
                  <p className="text-slate-300 mb-6">
                    Common questions about this module's content, answered.
                  </p>
                </Card>

                <div className="space-y-4">
                  {(module.faqs || []).map((faq, idx) => (
                    <Card key={idx} className="p-6">
                      <h3 className="text-lg font-semibold text-white mb-3 flex items-start gap-3">
                        <MessageSquare className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" />
                        {faq.question}
                      </h3>
                      <MarkdownText className="text-slate-300 leading-relaxed">
                        {faq.answer}
                      </MarkdownText>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Resources Page */}
            {currentPageData.type === 'resources' && (
              <div className="space-y-6">
                <Card className="p-6">
                  <h2 className="text-2xl font-bold text-white mb-4">Recommended Resources</h2>
                  <p className="text-slate-300 mb-6">
                    Dive deeper with these curated resources specific to this module's content.
                  </p>
                </Card>

                <div className="space-y-4">
                  {(module.resources || []).map((resource, idx) => (
                    <Card key={idx} className="p-6 hover:border-rose-500/50 transition-all" hover={true}>
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <BookOpen className="w-6 h-6 text-purple-400" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">
                            {resource.title}
                          </h3>
                          {resource.author && (
                            <p className="text-purple-300 text-sm font-medium mb-2">
                              by {resource.author}
                            </p>
                          )}
                          <p className="text-slate-300 text-sm mb-2">
                            {resource.description}
                          </p>
                          {resource.why && (
                            <p className="text-slate-400 text-sm italic mb-3">
                              Why read this: {resource.why}
                            </p>
                          )}
                          <div className="flex items-center flex-wrap gap-3">
                            <span className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded">
                              {resource.type}
                            </span>
                            {resource.url && (
                              <a
                                href={resource.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-rose-400 hover:text-pink-400 text-sm flex items-center gap-1 transition-colors"
                              >
                                Visit Resource
                                <ArrowRight className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Takeaways Page */}
            {currentPageData.type === 'takeaways' && (
              <Card className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Key Takeaways</h2>

                {(module.keyTakeaways || module.content?.keyTakeaways) && (
                  <div className="space-y-4 mb-8">
                    {(module.keyTakeaways || module.content.keyTakeaways).map((takeaway, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-4 bg-violet-500/10 rounded-lg border border-violet-500/20">
                        <CheckCircle className="w-5 h-5 text-violet-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          {typeof takeaway === 'string' ? (
                            <MarkdownText className="text-slate-300">
                              {takeaway}
                            </MarkdownText>
                          ) : (
                            <>
                              {takeaway.title && (
                                <div className="font-semibold text-slate-200 mb-1">{takeaway.title}</div>
                              )}
                              <MarkdownText className="text-slate-300">
                                {takeaway.content}
                              </MarkdownText>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reflection Questions */}
                {(module.reflectionQuestions || module.content?.reflectionQuestions) && (
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                      <Lightbulb className="w-7 h-7 text-pink-400" />
                      Reflection Questions
                    </h3>
                    <p className="text-slate-300 mb-6">
                      Take a moment to reflect on what you've learned and how it applies to your own life and interactions.
                    </p>
                    <div className="space-y-4">
                      {(module.reflectionQuestions || module.content.reflectionQuestions).map((question, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className="flex gap-4 p-5 bg-gradient-to-r from-purple-500/10 to-violet-500/10 rounded-lg border border-purple-500/30 hover:border-purple-400/50 transition-all"
                        >
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center text-white font-bold shadow-lg">
                              {idx + 1}
                            </div>
                          </div>
                          <div className="flex-1 pt-1">
                            <MarkdownText className="text-slate-200 leading-relaxed">
                              {question}
                            </MarkdownText>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Next Steps Page */}
            {currentPageData.type === 'nextsteps' && (
              <Card className="p-8">
                <div className="mb-8">
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-6"
                  >
                    <motion.div
                      className="w-24 h-24 bg-gradient-to-br from-rose-500 via-pink-400 to-pink-500 rounded-3xl flex items-center justify-center shadow-2xl mx-auto mb-6"
                      whileHover={{ scale: 1.05, rotate: 5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                      style={{ boxShadow: '0 10px 40px rgba(20, 184, 166, 0.4), 0 0 60px rgba(212, 175, 55, 0.3)' }}
                    >
                      <ArrowRight className="w-12 h-12 text-white" />
                    </motion.div>
                    <h2 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-rose-500 via-cyan-300 to-pink-500 mb-4">
                      Next Steps
                    </h2>
                    <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-rose-400">
                      Where to Go From Here
                    </p>
                  </motion.div>
                </div>

                {(module.nextSteps || module.content?.nextSteps) && (
                  <div>
                    {(() => {
                      const nextSteps = module.nextSteps || module.content.nextSteps;

                      // Handle object structure with immediate/thisWeek/longTerm (Module 03+ format)
                      if (nextSteps && typeof nextSteps === 'object' && !Array.isArray(nextSteps) && (nextSteps.immediate || nextSteps.thisWeek || nextSteps.longTerm)) {
                        return (
                          <div className="space-y-8">
                            {/* Immediate Steps */}
                            {nextSteps.immediate && nextSteps.immediate.length > 0 && (
                              <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                              >
                                <div className="flex items-center gap-3 mb-4">
                                  <motion.div
                                    className="w-12 h-12 bg-gradient-to-br from-violet-400 via-fuchsia-500 to-violet-600 rounded-xl flex items-center justify-center shadow-xl"
                                    whileHover={{ scale: 1.1, rotate: 10 }}
                                    style={{ boxShadow: '0 8px 30px rgba(34, 197, 94, 0.5)' }}
                                  >
                                    <Zap className="w-6 h-6 text-white" />
                                  </motion.div>
                                  <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-fuchsia-400">
                                    Immediate Actions (This Week)
                                  </h3>
                                </div>
                                <div className="space-y-3">
                                  {nextSteps.immediate.map((step, idx) => (
                                    <motion.div
                                      key={idx}
                                      initial={{ opacity: 0, x: -20 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: 0.2 + idx * 0.08 }}
                                      whileHover={{ scale: 1.02, x: 5 }}
                                      className="group relative flex items-start gap-4 p-5 bg-gradient-to-r from-violet-500/20 via-fuchsia-500/15 to-violet-500/20 rounded-xl border-2 border-violet-500/40 hover:border-rose-400/70 transition-all cursor-default overflow-hidden"
                                      style={{ boxShadow: '0 4px 20px rgba(244, 63, 94, 0.15)' }}
                                    >
                                      {/* Animated background shimmer */}
                                      <motion.div
                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-violet-400/10 to-transparent"
                                        animate={{ x: ['-100%', '200%'] }}
                                        transition={{ duration: 3, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
                                      />
                                      <div className="relative z-10 w-6 h-6 bg-gradient-to-br from-violet-400 to-fuchsia-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-lg group-hover:shadow-violet-400/50 transition-shadow">
                                        <CheckCircle className="w-4 h-4 text-white" />
                                      </div>
                                      <MarkdownText className="relative z-10 text-slate-100 group-hover:text-white transition-colors leading-relaxed">
                                        {step}
                                      </MarkdownText>
                                    </motion.div>
                                  ))}
                                </div>
                              </motion.div>
                            )}

                            {/* This Week Steps */}
                            {nextSteps.thisWeek && nextSteps.thisWeek.length > 0 && (
                              <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                              >
                                <div className="flex items-center gap-3 mb-4">
                                  <motion.div
                                    className="w-12 h-12 bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 rounded-xl flex items-center justify-center shadow-xl"
                                    whileHover={{ scale: 1.1, rotate: -10 }}
                                    style={{ boxShadow: '0 8px 30px rgba(6, 182, 212, 0.5)' }}
                                  >
                                    <Target className="w-6 h-6 text-white" />
                                  </motion.div>
                                  <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-pink-400">
                                    Practice This Week
                                  </h3>
                                </div>
                                <div className="space-y-3">
                                  {nextSteps.thisWeek.map((step, idx) => (
                                    <motion.div
                                      key={idx}
                                      initial={{ opacity: 0, x: -20 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: 0.3 + idx * 0.08 }}
                                      whileHover={{ scale: 1.02, x: 5 }}
                                      className="group relative flex items-start gap-4 p-5 bg-gradient-to-r from-rose-500/20 via-pink-500/15 to-purple-500/20 rounded-xl border-2 border-rose-500/40 hover:border-rose-400/70 transition-all cursor-default overflow-hidden"
                                      style={{ boxShadow: '0 4px 20px rgba(6, 182, 212, 0.15)' }}
                                    >
                                      {/* Animated background shimmer */}
                                      <motion.div
                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-pink-400/10 to-transparent"
                                        animate={{ x: ['-100%', '200%'] }}
                                        transition={{ duration: 3, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
                                      />
                                      <div className="relative z-10 w-6 h-6 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-lg group-hover:shadow-rose-400/50 transition-shadow">
                                        <CheckCircle className="w-4 h-4 text-white" />
                                      </div>
                                      <MarkdownText className="relative z-10 text-slate-100 group-hover:text-white transition-colors leading-relaxed">
                                        {step}
                                      </MarkdownText>
                                    </motion.div>
                                  ))}
                                </div>
                              </motion.div>
                            )}

                            {/* Long-term Steps */}
                            {nextSteps.longTerm && nextSteps.longTerm.length > 0 && (
                              <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                              >
                                <div className="flex items-center gap-3 mb-4">
                                  <motion.div
                                    className="w-12 h-12 bg-gradient-to-br from-purple-400 via-violet-500 to-fuchsia-600 rounded-xl flex items-center justify-center shadow-xl"
                                    whileHover={{ scale: 1.1, rotate: 10 }}
                                    style={{ boxShadow: '0 8px 30px rgba(168, 85, 247, 0.5)' }}
                                  >
                                    <TrendingUp className="w-6 h-6 text-white" />
                                  </motion.div>
                                  <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-fuchsia-400">
                                    Long-term Development
                                  </h3>
                                </div>
                                <div className="space-y-3">
                                  {nextSteps.longTerm.map((step, idx) => (
                                    <motion.div
                                      key={idx}
                                      initial={{ opacity: 0, x: -20 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: 0.4 + idx * 0.08 }}
                                      whileHover={{ scale: 1.02, x: 5 }}
                                      className="group relative flex items-start gap-4 p-5 bg-gradient-to-r from-purple-500/20 via-violet-500/15 to-fuchsia-500/20 rounded-xl border-2 border-purple-500/40 hover:border-purple-400/70 transition-all cursor-default overflow-hidden"
                                      style={{ boxShadow: '0 4px 20px rgba(168, 85, 247, 0.15)' }}
                                    >
                                      {/* Animated background shimmer */}
                                      <motion.div
                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-400/10 to-transparent"
                                        animate={{ x: ['-100%', '200%'] }}
                                        transition={{ duration: 3, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
                                      />
                                      <div className="relative z-10 w-6 h-6 bg-gradient-to-br from-purple-400 to-fuchsia-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-lg group-hover:shadow-purple-400/50 transition-shadow">
                                        <CheckCircle className="w-4 h-4 text-white" />
                                      </div>
                                      <MarkdownText className="relative z-10 text-slate-100 group-hover:text-white transition-colors leading-relaxed">
                                        {step}
                                      </MarkdownText>
                                    </motion.div>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </div>
                        );
                      }

                      // Handle object structure with recommendations (Module 01 & 02 DDD format)
                      if (nextSteps && typeof nextSteps === 'object' && !Array.isArray(nextSteps) && nextSteps.recommendations) {
                        return (
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                          >
                            {nextSteps.description && (
                              <div className="p-4 bg-gradient-to-r from-indigo-500/15 via-purple-500/15 to-pink-500/15 border-l-4 border-indigo-500 rounded-r-xl mb-6">
                                <p className="text-slate-200 text-lg leading-relaxed">{nextSteps.description}</p>
                              </div>
                            )}
                            <div className="space-y-3">
                              {nextSteps.recommendations.map((step, idx) => (
                                <motion.div
                                  key={idx}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.1 + idx * 0.08 }}
                                  whileHover={{ scale: 1.02, x: 5 }}
                                  className="group relative flex items-start gap-4 p-5 bg-gradient-to-r from-rose-500/20 via-pink-500/15 to-pink-500/20 rounded-xl border-2 border-rose-500/40 hover:border-rose-500/70 transition-all cursor-default overflow-hidden"
                                  style={{ boxShadow: '0 4px 20px rgba(20, 184, 166, 0.15)' }}
                                >
                                  {/* Animated background shimmer */}
                                  <motion.div
                                    className="absolute inset-0 bg-gradient-to-r from-transparent via-conversation-teal/10 to-transparent"
                                    animate={{ x: ['-100%', '200%'] }}
                                    transition={{ duration: 3, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
                                  />
                                  <div className="relative z-10 w-6 h-6 bg-gradient-to-br from-rose-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-lg group-hover:shadow-rose-500/50 transition-shadow">
                                    <CheckCircle className="w-4 h-4 text-white" />
                                  </div>
                                  <MarkdownText className="relative z-10 text-slate-100 group-hover:text-white transition-colors leading-relaxed">
                                    {step}
                                  </MarkdownText>
                                </motion.div>
                              ))}
                            </div>
                          </motion.div>
                        );
                      }

                      // Handle array structure (older modules)
                      if (Array.isArray(nextSteps)) {
                        return (
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                          >
                            <div className="space-y-3">
                              {nextSteps.map((step, idx) => (
                                <motion.div
                                  key={idx}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.1 + idx * 0.08 }}
                                  whileHover={{ scale: 1.02, x: 5 }}
                                  className="group relative flex items-start gap-4 p-5 bg-gradient-to-r from-rose-500/20 via-pink-500/15 to-pink-500/20 rounded-xl border-2 border-rose-500/40 hover:border-rose-500/70 transition-all cursor-default overflow-hidden"
                                  style={{ boxShadow: '0 4px 20px rgba(20, 184, 166, 0.15)' }}
                                >
                                  {/* Animated background shimmer */}
                                  <motion.div
                                    className="absolute inset-0 bg-gradient-to-r from-transparent via-conversation-teal/10 to-transparent"
                                    animate={{ x: ['-100%', '200%'] }}
                                    transition={{ duration: 3, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
                                  />
                                  <div className="relative z-10 w-6 h-6 bg-gradient-to-br from-rose-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-lg group-hover:shadow-rose-500/50 transition-shadow">
                                    <CheckCircle className="w-4 h-4 text-white" />
                                  </div>
                                  <MarkdownText className="relative z-10 text-slate-100 group-hover:text-white transition-colors leading-relaxed">
                                    {step}
                                  </MarkdownText>
                                </motion.div>
                              ))}
                            </div>
                          </motion.div>
                        );
                      }

                      return null;
                    })()}
                  </div>
                )}

                {/* Revise with Compass CTA */}
                <div className="mt-6 text-center">
                  <button
                    onClick={() => {
                      saveModuleContext({ id: module?.id, title: module?.title, section: 'Next Steps' });
                      window.location.href = '/coach';
                    }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-emerald-300 hover:text-emerald-100 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-lg transition"
                  >
                    <Sparkles className="w-4 h-4" />
                    Revise with Compass
                  </button>
                </div>

                {/* Complete Module Button */}
                {progressPercentage >= 80 && (
                  <div className="mt-8 pt-8 border-t border-slate-700">
                    <div className="text-center">
                      <p className="text-slate-300 mb-4 text-lg">
                        Ready to celebrate your achievement?
                      </p>
                      <Button
                        onClick={handleModuleComplete}
                        size="lg"
                        className="bg-gradient-to-r from-rose-500 to-pink-500 hover:shadow-2xl hover:shadow-rose-500/50 transition-all"
                      >
                        <Award className="w-5 h-5 mr-2" />
                        Complete Module & Get Certificate
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Bottom Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="relative z-10 mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4"
        >
          <Button
            variant="ghost"
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className="flex items-center justify-center gap-2 order-2 sm:order-1"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Previous</span>
            <span className="sm:hidden">Back</span>
          </Button>

          <Button
            onClick={handleContinue}
            disabled={currentPage === pages.length - 1 && moduleIsCompleted}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-rose-500 to-pink-500 text-white px-6 sm:px-8 py-3 text-sm sm:text-base font-semibold hover:shadow-lg hover:shadow-rose-500/30 transition-all order-1 sm:order-2"
          >
            {currentPage === pages.length - 1 ? (
              <>
                <Award className="w-4 h-4 sm:w-5 sm:h-5" />
                Complete Module
              </>
            ) : (
              <>
                <span className="hidden sm:inline">Mark as Complete & Continue</span>
                <span className="sm:hidden">Continue</span>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </>
            )}
          </Button>
        </motion.div>

        {/* Celebration Modal */}
        <CelebrationModal
          show={showCelebration}
          onClose={() => setShowCelebration(false)}
          message={celebrationMessage}
        />

        {/* Completion Certificate */}
        <CompletionCertificate
          show={showCertificate}
          onClose={() => {
            setShowCertificate(false);
            onComplete?.();
          }}
          module={module}
          userName="Learner"
        />

        {/* Course Mastery Certificate */}
        <CourseMasteryCertificate
          show={showCourseMastery}
          onClose={() => {
            setShowCourseMastery(false);
            onComplete?.();
          }}
          userName="Learner"
          completedModules={useGamificationStore.getState().completedModules}
          totalPoints={useGamificationStore.getState().totalPoints}
        />
      </div>
    </div>
  );
};

export default ConversationModuleDetail;